/**
 * Shared domain types and constants for the Inventory Storage Locations feature.
 *
 * These mirror the data models and the `@tcvs/shared` types block in
 * `.kiro/specs/inventory-storage-locations/design.md`. They are the single
 * source of truth for the warehouse containment hierarchy — Rack → Shelf →
 * Box → Row → Storage_Batch → Card — and for QR tokens, location resolution, the audit
 * trail, picklist results, and the scan-driven putaway flow. `cdk`, `api`, and
 * `frontend` import these shapes rather than redeclaring them (per
 * `structure.md`: cross-package types live in `@tcvs/shared`).
 *
 * Two load-bearing principles drive these shapes:
 *   1. Identity is separate from location — every entity stores only its
 *      immediate `parentId`; a Card stores only its immediate `placementBatchId`
 *      while STORED. No denormalized Location_Path is ever stored; it is
 *      resolved on demand by walking the parent chain.
 *   2. Labels are opaque and stable — a QR_Token encodes only a type tag and an
 *      opaque, immutable id (a ULID for entities, the existing UUID for cards),
 *      never a location, so moving a container never invalidates a printed label.
 *
 * Nothing in this file imports an AWS SDK, a Node-only API, or a browser-only
 * API, so it is safe to use from any runtime.
 *
 * Validates: Requirements 1.6, 2.1, 2.2, 9.1, 13.5, 17.1, 17.2, 18.1, 18.2,
 * 19.1, 19.2, 19.8
 */

import type { ChecklistContextSuggestion, RunChecklistContext } from './checklist';

// ---------------------------------------------------------------------------
// Entity + placement primitives
// ---------------------------------------------------------------------------

/** The node types in the containment hierarchy. */
export type StorageEntityType = 'RACK' | 'SHELF' | 'BOX' | 'ROW' | 'BATCH';

/**
 * A Card's placement state. `STORED` ⇒ resident in a Storage_Batch; `PULLED` ⇒
 * removed from storage. A card that has never been put away has no placement
 * state at all (modeled as `NONE` in {@link CardPlacement}).
 */
export type PlacementState = 'STORED' | 'PULLED';

/**
 * A fixed physical location that holds Shelves. A Rack is the root of the
 * warehouse hierarchy, so it never carries a `parentId`. Mirrors the
 * `tcvs-storage-entities` item for `entityType = 'RACK'`.
 */
export interface Rack {
  /** Opaque, immutable id, e.g. `tcvs:rack:<ULID>`. */
  entityId: string;
  entityType: 'RACK';
  /** Operator-assigned human-readable code; unique across Racks. */
  rackCode: string;
  /** ISO 8601 creation timestamp. */
  createdAt: string;
  /** Cognito sub of the Operator that created the Rack. */
  createdBy: string;
  /** Optional configured max immediate children. */
  capacity?: number;
  /** When true, child count / capacity are reported. */
  occupancyEnabled?: boolean;
  /** Maintained only when `occupancyEnabled`. */
  childCount?: number;
}

/**
 * A numbered physical location that resides in a Rack and holds Boxes. Existing
 * legacy shelves may have no `parentId` until they are assigned to a Rack.
 * Mirrors the `tcvs-storage-entities` item
 * for `entityType = 'SHELF'`.
 */
export interface Shelf {
  /** Opaque, immutable id, e.g. `tcvs:shelf:<ULID>` (Req 1.1, 1.4). */
  entityId: string;
  entityType: 'SHELF';
  /** Immediate parent Rack id; absent for legacy/unassigned Shelves. */
  parentId?: string;
  /** Operator-assigned human-readable code; unique across Shelves (Req 1.5). */
  shelfCode: string;
  /** ISO 8601 creation timestamp (Req 1.1). */
  createdAt: string;
  /** Cognito sub of the Operator that created the Shelf. */
  createdBy: string;
  /** Optional configured max immediate children (Req 17.2). */
  capacity?: number;
  /** When true, child count / capacity are reported (Req 17.1, 17.4). */
  occupancyEnabled?: boolean;
  /** Maintained only when `occupancyEnabled` (Req 17.1). */
  childCount?: number;
}

/**
 * A movable container that resides on exactly one Shelf and holds Rows. Moving
 * a Box relocates every Row, Storage_Batch, and Card it
 * holds via a single `parentId` update. Mirrors the `tcvs-storage-entities`
 * item for `entityType = 'BOX'`.
 */
export interface Box {
  /** Opaque, immutable id, e.g. `tcvs:box:<ULID>` (Req 1.2, 1.4). */
  entityId: string;
  entityType: 'BOX';
  /** Immediate parent Shelf id; absent when not yet put away (Req 2.2, 1.6). */
  parentId?: string;
  /** ISO 8601 creation timestamp (Req 1.2). */
  createdAt: string;
  /** Cognito sub of the Operator that created the Box. */
  createdBy: string;
  /** Optional configured max immediate children (Req 17.2). */
  capacity?: number;
  /** When true, child count / capacity are reported (Req 17.1, 17.4). */
  occupancyEnabled?: boolean;
  /** Maintained only when `occupancyEnabled` (Req 17.1). */
  childCount?: number;
}

/**
 * A row inside a Box. Rows are the immediate parent of Storage_Batches, allowing
 * one Box to be subdivided into one or more scannable positions.
 */
export interface StorageRow {
  /** Opaque, immutable id, e.g. `tcvs:row:<ULID>`. */
  entityId: string;
  entityType: 'ROW';
  /** Immediate parent Box id; absent when not yet put away. */
  parentId?: string;
  /** Operator-assigned label within a Box, e.g. `Row 1` or `A`. */
  rowCode?: string;
  /** ISO 8601 creation timestamp. */
  createdAt: string;
  /** Cognito sub of the Operator that created the Row. */
  createdBy: string;
  /** Optional configured max immediate children. */
  capacity?: number;
  /** When true, child count / capacity are reported. */
  occupancyEnabled?: boolean;
  /** Maintained only when `occupancyEnabled`. */
  childCount?: number;
}

/**
 * A movable grouping of cards put away together, residing in exactly one Row.
 * Corresponds one-to-one with a `Capture_Run` via `sourceRunId`. An
 * Unplaced_Storage_Batch has no `parentId` (its run is active or it awaits
 * putaway). Mirrors the `tcvs-storage-entities` item for `entityType = 'BATCH'`.
 */
export interface StorageBatch {
  /** Opaque, immutable id, e.g. `tcvs:batch:<ULID>` (Req 1.3, 1.4). */
  entityId: string;
  entityType: 'BATCH';
  /** Immediate parent Row id; absent for an Unplaced_Storage_Batch. Legacy records may still point directly to a Box. */
  parentId?: string;
  /**
   * The `Capture_Run` `runId` (UUID v4) this batch corresponds to. Present on
   * run-originated batches; absent on manually-created batches. Records the
   * one-to-one run↔batch linkage without making the run id the entity key
   * (Req 18.1, 18.2).
   */
  sourceRunId?: string;
  /**
   * `OPEN` while the Capture_Run is active and accepting server-side
   * auto-membership; `CLOSED` after End Run. Absent for manual batches
   * (Req 18.4, 18.5).
   */
  runState?: 'OPEN' | 'CLOSED';
  /**
   * The checklist selected by a reviewer for this batch. It constrains
   * non-authoritative checklist suggestions only; it never asserts any card's
   * identity and is absent for unconfigured batches.
   */
  checklistContext?: RunChecklistContext;
  /**
   * Legacy batch-aggregated OCR hint. Retained for backward compatibility;
   * new OCR checklist matching is stored per card.
   */
  checklistContextSuggestion?: ChecklistContextSuggestion;
  /** ISO 8601 creation timestamp (Req 1.3). */
  createdAt: string;
  /** Operator sub, or a system-actor marker for run-minted batches (Req 18.1, 18.6). */
  createdBy: string;
  /** Optional configured max immediate children (Req 17.2). */
  capacity?: number;
  /** When true, child count / capacity are reported (Req 17.1, 17.4). */
  occupancyEnabled?: boolean;
  /** Maintained only when `occupancyEnabled` (Req 17.1). */
  childCount?: number;
}

/** Any node in the containment hierarchy. */
export type StorageEntity = Rack | Shelf | Box | StorageRow | StorageBatch;

/**
 * Read-time operational status for a Storage_Batch. This is not persisted on
 * the Storage_Entity; API read routes derive it from the batch's stored cards
 * and placement so operators can see whether the batch still needs Image
 * Approval, SKU write-back, or physical putaway.
 */
export interface StorageBatchWorkflowStatus {
  cardCount: number;
  /** Unified identity/image review status. Present on current API responses. */
  review?: {
    startedCount: number;
    readyCount: number;
    /** Identified cards awaiting image approval; absent on older API responses. */
    awaitingImageApprovalCount?: number;
    imageIssueCount: number;
    completedCount: number;
    missingCount: number;
  };
  reviewComplete?: boolean;
  preliminaryImageReview?: {
    reviewedCount: number;
    approvedCount: number;
    issueCount: number;
    missingCount: number;
  };
  preliminaryImageReviewComplete?: boolean;
  imageApproval: {
    approvedCount: number;
    pendingCount: number;
    rejectedCount: number;
    missingCount: number;
  };
  imageApprovalComplete: boolean;
  /** Historical legacy SKU coverage only; never a workflow completion requirement. */
  sku: {
    assignedCount: number;
    missingCount: number;
  };
  /** @deprecated Historical coverage; completion depends on review and putaway. */
  skuComplete: boolean;
  putAway: boolean;
}

/**
 * The association between a Card and its immediate parent Storage_Batch, stored
 * as attributes on the existing `tcvs-cards` item (not denormalized into a
 * path). `state = 'NONE'` means the Card has never been put away.
 */
export interface CardPlacement {
  cardId: string;
  state: PlacementState | 'NONE';
  /** Immediate parent Storage_Batch id; present only while `STORED`. */
  batchId?: string;
  /** ISO 8601 timestamp of the last placement change. */
  updatedAt?: string;
  /** 1-based current position among STORED cards in the batch, compacted after picks. */
  currentBatchPosition?: number;
  /** Total number of currently STORED cards in the same batch. */
  currentBatchSize?: number;
}

/** Physical append order shared by inventory positions and batch review. */
export function compareBatchPlacementOrder(
  left: { cardId: string; placementUpdatedAt?: string },
  right: { cardId: string; placementUpdatedAt?: string },
): number {
  const byTime = (left.placementUpdatedAt ?? '').localeCompare(right.placementUpdatedAt ?? '');
  return byTime !== 0 ? byTime : left.cardId.localeCompare(right.cardId);
}

// ---------------------------------------------------------------------------
// Location resolution
// ---------------------------------------------------------------------------

/**
 * A Card's fully resolved physical path, produced by walking the parent chain
 * (card → batch → row → box → shelf → rack). Never stored denormalized.
 */
export interface LocationPath {
  rack?: { entityId: string; rackCode: string };
  shelf: { entityId: string; shelfCode: string };
  box: { entityId: string };
  row?: { entityId: string; rowCode?: string };
  batch: { entityId: string };
}

/**
 * The location-resolution result for a `STORED` Card whose parent chain is not
 * fully shelved. At minimum the batch is named. When the Storage_Batch has
 * already been put into a Box, the partial location also names that Box even if
 * the Box is not yet on a Shelf.
 */
export interface PartialLocation {
  rack?: { entityId: string; rackCode: string };
  shelf?: { entityId: string; shelfCode: string };
  box?: { entityId: string };
  row?: { entityId: string; rowCode?: string };
  batch: { entityId: string };
}

/** Current physical pick position inside a Storage_Batch. */
export interface CurrentBatchPosition {
  /** 1-based current position among currently STORED cards. */
  position: number;
  /** Total number of currently STORED cards in the batch. */
  total: number;
}

/**
 * The discriminated result of resolving a single Card's location.
 *
 * - `STORED` — full Shelf → Box → Row → Storage_Batch path, optionally rooted at a Rack.
 * - `STORED_UNPLACED` — batch known, but the full path is not available yet.
 * - `PULLED` — Card removed from storage; no path (Req 5.2, 6.4).
 * - `NOT_PLACED` — Card never put away (Req 6.5).
 * - `CARD_UNKNOWN` — no `Card_Record` for the requested id.
 */
export type LocationResolution =
  | { status: 'STORED'; path: LocationPath; batchPosition?: CurrentBatchPosition }
  | ({ status: 'STORED_UNPLACED'; batchPosition?: CurrentBatchPosition } & PartialLocation)
  | { status: 'PULLED' }
  | { status: 'NOT_PLACED' }
  | { status: 'CARD_UNKNOWN' };

// ---------------------------------------------------------------------------
// Audit trail
// ---------------------------------------------------------------------------

/** The kind of mutating storage operation recorded in the audit trail. */
export type StorageOpType = 'PUTAWAY' | 'MOVE' | 'PICK';

/**
 * An immutable audit record of a Putaway, Move, or Pick. Mirrors the
 * `tcvs-storage-transactions` item. Written once, never updated or deleted
 * (Req 11.1, 11.2, 11.4).
 */
export interface StorageTransaction {
  /** The affected entity or card id (the subject whose history this is). */
  entityRef: string;
  /** ULID, unique per record. */
  txnId: string;
  opType: StorageOpType;
  /** Authenticated Operator sub (Req 11.4). */
  actorId: string;
  /** ISO 8601 operation time. */
  timestamp: string;
  /** Source parent (null for a first putaway). */
  fromParentId?: string | null;
  /** Destination parent (null for a pick). */
  toParentId?: string | null;
  /** Placement state before a Card pick (`STORED`). */
  fromState?: PlacementState;
  /** Placement state after a Card pick (`PULLED`). */
  toState?: PlacementState;
}

// ---------------------------------------------------------------------------
// QR tokens + labels
// ---------------------------------------------------------------------------

/** The type tag carried by a QR_Token. */
export type QrTokenType = 'rack' | 'shelf' | 'box' | 'row' | 'batch' | 'card';

/**
 * A parsed QR_Token: a type tag plus an opaque id (ULID for entities, UUID for
 * cards). `raw` is the original colon-delimited string. Never encodes a
 * location (Req 9.1).
 */
export interface QrToken {
  type: QrTokenType;
  id: string;
  raw: string;
}

// ---------------------------------------------------------------------------
// Run → Storage_Batch resolution (run-batch-label-printing feature)
// ---------------------------------------------------------------------------

/**
 * The Run_Batch_Resolution response: the Storage_Batch that corresponds to a
 * `Capture_Run`, returned by `GET /storage/batches/by-run/{runId}`. Carries at
 * least the batch's Storage_Entity_Id and its QR_Token (Req 8.1). The token is
 * produced by the SAME generator the labels route uses, so it equals the
 * labels-route token for this batch (Req 1.3). Never carries a per-batch image
 * (Req 1.5).
 *
 * Mirrors the existing label-response envelope (`{ entityId, entityType, token,
 * parsed }`) plus `sourceRunId`, so the SPA can reuse the same label render
 * against either shape. The single source of truth for this wire contract —
 * `api` and `frontend` import it rather than redeclaring it (per `structure.md`).
 */
export interface RunBatchResolution {
  /** The Storage_Batch's opaque, immutable id, `tcvs:batch:<ULID>` (Req 1.1). */
  entityId: string;
  /** Always `'BATCH'` — the resolved entity is a Storage_Batch. */
  entityType: 'BATCH';
  /** The QR_Token; for a batch this equals `entityId` (Req 1.1, 1.3). */
  token: string;
  /** The parsed token (type tag + opaque id + raw), as the labels route returns. */
  parsed: QrToken;
  /** The `Capture_Run` runId this batch corresponds to; echoed for the caller. */
  sourceRunId?: string;
}

// ---------------------------------------------------------------------------
// Picklist
// ---------------------------------------------------------------------------

/**
 * A per-card picklist result, keyed back to the input id. There is exactly one
 * entry per input id (Req 7.1); an unknown id is reported as `UNKNOWN` rather
 * than failing the whole picklist (Req 7.4).
 *
 * Each entry additionally carries an optional, best-effort card preview
 * (`thumbnailUrl` + `shortCode`) so the picklist can show the operator what to
 * pull. These are absent for `UNKNOWN` entries and for cards with no image, and
 * never cause the picklist to fail (the preview is enriched server-side after
 * resolution).
 */
export type PicklistEntry = (
  | { cardId: string; status: 'STORED'; path: LocationPath }
  | ({ cardId: string; status: 'STORED_UNPLACED' } & PartialLocation)
  | { cardId: string; status: 'PULLED' }
  | { cardId: string; status: 'NOT_PLACED' }
  | { cardId: string; status: 'UNKNOWN' }
) & {
  /** Best-effort presigned front-image thumbnail (TTL ≤ 900 s). */
  thumbnailUrl?: string;
  /** The card's human-friendly short code, when present. */
  shortCode?: string;
};

// ---------------------------------------------------------------------------
// Container listing (storage-container-listing feature)
// ---------------------------------------------------------------------------

/**
 * The listable container `{type}` path segment, in its plural collection form
 * (mirrors the existing `/storage/{type}` collection routes). One of
 * `racks`, `shelves`, `boxes`, `rows`, or `batches`.
 */
export type ListableContainerType = 'racks' | 'shelves' | 'boxes' | 'rows' | 'batches';

/**
 * A compact, best-effort card image preview attached only to a Storage_Batch
 * listing when explicitly requested. The URLs are presigned and therefore
 * short-lived; callers must treat either side as optional.
 */
export interface BatchListingCardPreview {
  cardId: string;
  thumbnailUrl?: string;
  backThumbnailUrl?: string;
  shortCode?: string;
}

/**
 * One enumerated entry in a {@link ContainerListing}. Minimal by design
 * (Req 10.3): `entityId` + `entityType` for every entry — enough to request
 * that entity's Label via `GET /storage/{type}/{id}/labels` (Req 1.5, 10.1) —
 * plus type-specific display fields and the immediate parent needed by the
 * Inventory hierarchy. A full Location_Path is never carried or denormalized.
 */
export interface ContainerListingEntry {
  entityId: string;
  entityType: StorageEntityType;
  /** Immediate containing Storage_Entity, absent for a root or unplaced entity. */
  parentId?: string;
  /** Present only for racks. */
  rackCode?: string;
  /** Present only for shelves (Req 1.6 of storage-container-listing). */
  shelfCode?: string;
  /** Present only for rows. */
  rowCode?: string;
  /**
   * The source `Capture_Run` id, present only for run-originated
   * Storage_Batches (run-batch-label-printing Req 6.1). Absent for manual
   * batches and for shelves/boxes (Req 6.2). Projected from `GSI4`, never
   * base-table-fetched.
   */
  sourceRunId?: string;
  /** Present only for Storage_Batch rows when the API can derive it. */
  batchStatus?: StorageBatchWorkflowStatus;
  /** Present only for Capture_Run-originated Storage_Batches. */
  runState?: 'OPEN' | 'CLOSED';
  /** A small ordered sample for the batch list image stack and hover gallery. */
  cardPreviews?: BatchListingCardPreview[];
}

/**
 * One page of enumerated containers of a requested Container_Type, returned by
 * the Storage_Service listing route (Req 1.1, 2.2, 9.1). The single source of
 * truth for the listing wire contract — `api`, `cdk`, and `frontend` import it
 * rather than redeclaring it.
 */
export interface ContainerListing {
  type: ListableContainerType;
  items: ContainerListingEntry[];
  /** Opaque continuation cursor; present only when more pages remain (Req 2.2, 2.3). */
  nextPageToken?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** The literal prefix that opens every QR_Token: `tcvs:<type>:<id>`. */
export const QR_TOKEN_PREFIX = 'tcvs';

/** The maximum number of cards a single picklist may resolve (Req 7.2). */
export const MAX_PICKLIST_SIZE = 500;

/** The page size for paginated container-contents listings (Req 8.4). */
export const CONTENTS_PAGE_SIZE = 100;

/**
 * The maximum number of Storage_Entities returned in one Container_Listing page
 * (the Listing_Page_Size term, Req 2.1). Kept distinct from
 * {@link CONTENTS_PAGE_SIZE}: listing and contents are independent concerns that
 * may tune their page sizes separately, even though they currently coincide.
 */
export const LISTING_PAGE_SIZE = 100;

// ---------------------------------------------------------------------------
// Scan-driven putaway (Requirement 19) — a thin sequencing layer over putaway/move
// ---------------------------------------------------------------------------

/**
 * The request body for the guided scan-driven putaway flow. The Operator scans
 * tokens in the fixed order Storage_Batch → Row. This introduces no new
 * placement semantics; it only sequences the existing putaway operation.
 */
export interface ScanPutawayRequest {
  /** Step 1 (BATCH) — required. */
  batchToken: string;
  /** Step 2 (ROW) — required. */
  rowToken: string;
  /** Legacy clients may still post a box token; ignored by the new flow. */
  boxToken?: string;
  /** Legacy clients may still post a shelf token; ignored by the new flow. */
  shelfToken?: string;
  /** Legacy override flag; ignored by the new flow. */
  override?: boolean;
}
